import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  TablePagination,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import { Price } from "../../../../redux/types/types";

interface PricesListProps {
  prices: Price[];
  totalPrices: number;
  page: number;
  rowsPerPage: number;
  orderBy: keyof Price | any;
  order: "asc" | "desc";
  onRequestSort: (property: keyof Price) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEditPrice: (price: Price) => void;
  onDeletePrice: (priceId: number) => void;
}

const PricesList: React.FC<PricesListProps> = ({
  prices,
  totalPrices,
  page,
  rowsPerPage,
  orderBy,
  order,
  onRequestSort,
  onChangePage,
  onChangeRowsPerPage,
  onEditPrice,
  onDeletePrice,
}) => {
  const createSortHandler = (property: keyof Price) => () => {
    onRequestSort(property);
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Assuming amount is stored in cents
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(date));
  };

  const getPriceTypeLabel = (price: Price) => {
    if (price.specialType && price.specialType.trim()) {
      return `${price.specialType} (${price.numberOfClasses} classes)`;
    }
    return `${price.numberOfClasses} classes`;
  };

  return (
    <Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "numberOfClasses"}
                  direction={orderBy === "numberOfClasses" ? order : "asc"}
                  onClick={createSortHandler("numberOfClasses")}
                >
                  <FormattedMessage id="admin.dashboard.priceType" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "amount"}
                  direction={orderBy === "amount" ? order : "asc"}
                  onClick={createSortHandler("amount")}
                >
                  <FormattedMessage id="admin.dashboard.amount" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "currency"}
                  direction={orderBy === "currency" ? order : "asc"}
                  onClick={createSortHandler("currency")}
                >
                  <FormattedMessage id="admin.dashboard.currency" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <FormattedMessage id="admin.dashboard.priceVisibility" />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={createSortHandler("createdAt")}
                >
                  <FormattedMessage id="admin.dashboard.createdAt" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <FormattedMessage id="admin.dashboard.actions" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    <FormattedMessage id="admin.dashboard.noPricesFound" />
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              prices.map((price) => (
                <TableRow key={price.id} hover>
                  <TableCell>
                    <Chip
                      label={getPriceTypeLabel(price)}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatAmount(price.amount, price.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={price.currency.toUpperCase()}
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {price.isAdminOnly ? (
                      <Chip
                        label="Admin Only"
                        color="warning"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="Public"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {formatDate(price.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => onEditPrice(price)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => onDeletePrice(price.id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalPrices}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        labelRowsPerPage={<FormattedMessage id="admin.dashboard.rowsPerPage" />}
      />
    </Box>
  );
};

export default PricesList;
